import {Component, inject, Input, OnInit} from '@angular/core';
import {ProgressService} from "../../../shared/services/progress.service";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {AppSettingsDataService} from "../../services/app-settings-data.service";
import {ActivatedRoute, NavigationExtras, Router} from "@angular/router";
import {ConfigurationService} from "../../../shared/services/configuration.service";
import {ConfigRouteEnum} from '../../../shared/enums/config-route.enum';
import {AppSettingsDetail} from "../models/app-settings.model";
import {
    ConfigDetailSkeletonComponent
} from "../../../shared/components/config-detail-skeleton/config-detail-skeleton.component";
import {DetailHeaderComponent} from "../../../shared/components/config-detail/detail-header/detail-header.component";
import {DatePipe, NgOptimizedImage, UpperCasePipe} from "@angular/common";
import {DiffEditorComponent, DiffEditorModel, EditorComponent} from "ngx-monaco-editor-v2";
import {FormsModule} from "@angular/forms";
import {ConfigDetailComponent} from "../../../shared/components/config-detail/config-detail.component";
import {DetailBodyComponent} from "../../../shared/components/config-detail/detail-body/detail-body.component";
import {SettingsDataService} from "../../../shared/services/settings-data.service";
import {SnackbarService} from "../../../shared/services/snackbar.service";
import {
    DetailHeaderActionComponent
} from "../../../shared/components/config-detail/detail-header-action/detail-header-action.component";
import {ConfigWriteUserService} from "../../../shared/services/config-write-user.service";
import {ConfirmationService} from "primeng/api";
import {DialogService} from "primeng/dynamicdialog";
import {
    ConfigReactivatePopupComponent
} from "../../../shared/components/config-reactivate-popup/config-reactivate-popup.component";
import {ReactivationsModel} from "../../services/models/app-settings-service.model";
import {CardModule} from "primeng/card";
import {ButtonModule} from "primeng/button";
import {InputTextModule} from "primeng/inputtext";
import {ToggleSwitch} from "primeng/toggleswitch";
import {SelectModule} from "primeng/select";
import {PanelModule} from "primeng/panel";
import {TableModule} from "primeng/table";

@Component({
    selector: 'app-app-settings-detail',
    standalone: true,
    imports: [
        ConfigDetailSkeletonComponent,
        DetailHeaderComponent,
        NgOptimizedImage,
        TranslatePipe,
        EditorComponent,
        FormsModule,
        ConfigDetailComponent,
        DetailBodyComponent,
        UpperCasePipe,
        DetailHeaderActionComponent,
        DiffEditorComponent,
        DatePipe,
        CardModule,
        ButtonModule,
        InputTextModule,
        ToggleSwitch,
        SelectModule,
        PanelModule,
        TableModule,
    ],
    templateUrl: './app-settings-detail.html',
    styleUrl: './app-settings-detail.scss',
})
export class AppSettingsDetailComponent implements OnInit {
    @Input() navigateBack?: { commands: any[], extras?: NavigationExtras };

    private readonly config = inject(ConfigurationService).configuration;
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly appSettingsService = inject(AppSettingsDataService);
    protected readonly progressService = inject(ProgressService);
    private readonly settingsDataService = inject(SettingsDataService);
    private readonly snackBar = inject(SnackbarService);
    private readonly translate = inject(TranslateService);
    private readonly router = inject(Router);
    private readonly configWriteUser = inject(ConfigWriteUserService);
    private readonly confirmationService = inject(ConfirmationService);
    private readonly dialogService = inject(DialogService);

    protected readonly logoUrl: string | null = this.config.app.defaultLogo;
    protected readonly ConfigRouteEnum = ConfigRouteEnum;

    protected id: number = 0;
    protected setting: AppSettingsDetail | undefined;
    protected reactivations: ReactivationsModel[] = [];
    protected dataSource: ReactivationsModel[] = [];
    protected reactivationColumns: string[] = ['id', 'reason', 'userName', 'timestamp'];

    protected isEditable: boolean = false;
    protected isAuthorized: boolean = this.configWriteUser.isAuthorized;
    protected searchName: string | null | undefined;

    protected originalCode: string | undefined;
    protected code: string | undefined;

    protected editorOptions = {
        language: 'json',
        readOnly: true,
        domReadOnly: true,
        automaticLayout: true,
        minimap: {enabled: false}
    };


    protected allSettings: any[] = [];
    protected selectedFirstSetting?: any;
    protected selectedSecondSetting?: any;
    protected selectedSecondSettingId?: number;
    protected isComparisonActive: boolean = false;
    protected isActive: boolean = false;
    protected isRevisionComparing: boolean = false;
    protected revisions: any[] = [];
    protected selectedRevision?: any;

    options = {
        readOnly: true,
        automaticLayout: true,
        ignoreTrimWhitespace: false,
        originalEditable: false,
        minimap: {enabled: false},
    };
    originalModel: DiffEditorModel = {
        code: '',
        language: 'json'
    };

    modifiedModel: DiffEditorModel = {
        code: '',
        language: 'json'
    };

    get versionOptions() {
        return [
            {data: this.id, label: '#' + this.id},
            ...this.allSettings.map(s => ({data: s.id, label: '#' + s.id})),
        ];
    }

    get compareOptions() {
        return [
            {data: undefined, label: this.translate.instant('appSettings.compareId')},
            ...this.allSettings.filter(s => s.id !== this.id).map(s => ({data: s.id, label: '#' + s.id})),
        ];
    }

    get compareOptionsStop() {
        return [
            {data: undefined, label: this.translate.instant('query.stop')},
            ...this.allSettings.filter(s => s.id !== this.id).map(s => ({data: s.id, label: '#' + s.id})),
        ];
    }

    get revisionOptions() {
        return [
            {data: undefined, label: this.translate.instant('appSettings.compareRev')},
            ...this.revisions.map(r => ({data: r, label: 'Rev ' + r.rev})),
        ];
    }

    get revisionOptionsStop() {
        return [
            {data: undefined, label: this.translate.instant('query.stop')},
            ...this.revisions.map(r => ({data: r, label: 'Rev ' + r.rev})),
        ];
    }

    ngOnInit(): void {
        this.subscribeToRoute();
    }

    private subscribeToRoute(): void {
        this.activatedRoute.paramMap.subscribe(paramMap => {
            const id = paramMap.get('id');
            const searchName = paramMap.get('searchName');

            if (id) {
                this.searchName = searchName
                this.id = Number.parseInt(id);
                this.loadAppSettings();
                this.loadEditPermission();
                this.getReactivation();
            }
        });
    }

    private loadAppSettings(): void {
        this.progressService.startLoading();
        this.selectedSecondSetting = undefined;
        this.selectedSecondSettingId = undefined;

        if (!this.searchName) {
            this.progressService.stopLoading();
            return
        }

        this.appSettingsService
            .findAllByApplicationName(this.searchName)
            .subscribe(
                {
                    next: (settings: any[]) => {
                        const foundSettting = settings.find((setting: any) => setting.id === this.id);

                        this.setting = foundSettting;
                        this.allSettings = settings
                            .filter((setting: any) => setting.applicationName === foundSettting.applicationName && setting.id !== foundSettting.id)
                            .sort((a: any, b: any) => a.id - b.id);
                        this.selectedFirstSetting = foundSettting;
                        this.buildDetail();
                        this.getRevision(foundSettting.applicationName, foundSettting.tenantName, foundSettting.identification);
                        this.progressService.stopLoading();

                    },
                    error: () => {
                        this.progressService.stopLoading();
                    }
                }
            )
    }

    private loadEditPermission(): void {
        this.configWriteUser.isAllowedToEditApplicationSettings(this.searchName).subscribe({
            next: (isAllowed) => {
                this.isEditable = isAllowed;
                this.editAppSettings();
            },
            error: () => {
                this.isEditable = false;
                this.editAppSettings();
            }
        });
    }

    private buildDetail(): void {
        if (this.setting?.applicationSettings) {
            try {
                this.code = JSON.stringify(JSON.parse(this.setting.applicationSettings), null, 2);
                this.originalCode = this.code;
                this.isActive = this.setting.active;
            } catch (e) {
                console.error(e);
                this.code = this.setting.applicationSettings;
                this.originalCode = this.code;
                this.isActive = this.setting.active;
            }
        }
    }

    private editAppSettings(): void {
        if (this.isEditable) {
            this.editorOptions = {
                ...this.editorOptions,
                readOnly: false,
                domReadOnly: false
            };
        }
    }

    protected saveAppSettings(): void {
        if (!this.setting || !this.code) {
            return;
        }

        const updatedSetting: any = {
            ...this.setting,
            applicationSettings: this.code
        };

        this.settingsDataService.updateSetting(updatedSetting).subscribe({
            next: (response) => {
                this.setting = response as AppSettingsDetail;
                this.buildDetail();
                this.snackBar.Success(this.translate.instant("snackbar.appSettings.saved"));
            },
            error: () => {
                this.snackBar.Error(this.translate.instant("snackbar.appSettings.save_error"));
            }
        });
    }

    protected resetAppSettings(): void {
        this.code = this.originalCode;
    }

    protected cloneConfiguration() {
        if (this.code === this.originalCode) {
            this.navigateToClonedConfiguration();
            return;
        }

        this.confirmationService.confirm({
            header: this.translate.instant('noSavePopup.title'),
            message: this.translate.instant('noSavePopup.message'),
            acceptButtonProps: {label: this.translate.instant('button.confirm'), severity: 'danger'},
            rejectButtonProps: {label: this.translate.instant('button.cancel'), severity: 'secondary', variant: 'text'},
            accept: () => this.navigateToClonedConfiguration(),
        });
    }

    private navigateToClonedConfiguration() {
        this.router.navigate(['/', ConfigRouteEnum.APP_SETTINGS, 'new'], {
            queryParams: {id: this.id}
        });
    }

    protected reactivate() {
        this.dialogService.open(ConfigReactivatePopupComponent, {
            header: this.translate.instant('reactivatePopup.title'),
            modal: true,
            closable: true,
            resizable: false,
            draggable: false,
            width: '32rem',
            breakpoints: {'640px': '90vw'},
        })?.onClose.subscribe(confirmed => {
            if (confirmed) {
                if (!this.setting?.id) {
                    return;
                }

                this.appSettingsService.reactivateSetting(this.setting.id, confirmed).subscribe({
                    next: () => {
                        this.snackBar.Success(this.translate.instant('snackbar.appSettings.saved'));
                        this.loadAppSettings();
                        this.getReactivation();
                    },
                    error: () => {
                        this.snackBar.Error(this.translate.instant('snackbar.appSettings.save_error'));
                    }
                });
            }
        });
    }

    appSettingsDetail(id: number) {
        this.router.navigate(['../', id], {relativeTo: this.activatedRoute});
    }

    secondVersion(id: number | undefined) {
        this.selectedSecondSettingId = id;
        const found = this.allSettings.find(setting => setting.id === id);
        this.selectedSecondSetting = found || undefined;
        this.compareVersion();
    }

    compareVersion() {
        if (this.selectedSecondSetting) {
            this.isRevisionComparing = false;
            this.isComparisonActive = true;
            let originalCode = this.code ?? this.selectedFirstSetting.applicationSettings;
            let modifiedCode = this.selectedSecondSetting.applicationSettings;

            try {
                originalCode = JSON.stringify(JSON.parse(originalCode), null, 2);
            } catch (e) {
                console.error(e);
            }

            try {
                modifiedCode = JSON.stringify(JSON.parse(modifiedCode), null, 2);
            } catch (e) {
                console.error(e);
            }

            this.originalModel = {
                code: originalCode,
                language: 'json'
            };
            this.modifiedModel = {
                code: modifiedCode,
                language: 'json'
            };
        } else {
            this.stopComparing();
        }
    }

    getRevision(applicationName: string, tenantName: string, identification: string) {
        this.appSettingsService.getRevisionByApplicationNameAndIdentificationAndTenantName(applicationName, tenantName, identification).subscribe({
            next: (revisions) => {
                this.revisions = revisions.sort((a: any, b: any) => (b.rev ?? 0) - (a.rev ?? 0));
            },
            error: () => {
                this.revisions = [];
            }
        });
    }

    protected onRevisionSelected(revision: any) {
        this.selectedRevision = revision;
        this.compareRevisions();
    }

    compareRevisions() {
        if (this.selectedRevision) {
            this.isRevisionComparing = true;
            this.isComparisonActive = true;
            let originalCode = this.code ?? this.selectedFirstSetting.applicationSettings;
            let modifiedCode = this.selectedRevision.applicationSettings;

            try {
                originalCode = JSON.stringify(JSON.parse(originalCode), null, 2);
            } catch (e) {
                console.error(e);
            }

            try {
                modifiedCode = JSON.stringify(JSON.parse(modifiedCode), null, 2);
            } catch (e) {
                console.error(e);
            }

            this.originalModel = {
                code: originalCode,
                language: 'json'
            };
            this.modifiedModel = {
                code: modifiedCode,
                language: 'json'
            };
        } else {
            this.stopComparing();
        }
    }

    stopComparing() {
        this.isComparisonActive = false;
        this.selectedSecondSetting = undefined;
        this.selectedSecondSettingId = undefined;
        this.selectedRevision = undefined;
    }

    private getReactivation() {
        this.appSettingsService.getReactivationsByAppSettingId(this.id).subscribe({
            next: (reactivations) => {
                this.reactivations = reactivations;
                this.dataSource = reactivations;
            },
            error: () => {
                this.reactivations = [];
                this.dataSource = [];
            }
        });
    }

    replaceOriginalCode() {
        const modifiedCode = this.modifiedModel.code;
        if (!modifiedCode) {
            return;
        }

        this.stopComparing();
        this.code = modifiedCode;
    }
}
